import { useEffect, useState } from 'react';

import {
  fetchCultureSelectorData,
  persistUserSessionData,
  useCultureSelectorData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import HeaderGC from '@skyscanner-internal/global-components/header';
import { loginModalInit } from '@skyscanner-internal/global-components/header/helpers';
import {
  SOURCES,
  type Source,
} from '@skyscanner-internal/global-components/login-form';
import { SubscriptionClient } from '@skyscanner-internal/global-components/login-form/helpers';
import logger from 'saddlebag-logger';
import { isLoggedIn, getProfilePicture } from 'saddlebag-user-management';

import { isBrowser } from '../../utils/isEnv';
import withErrorBoundary from '../ErrorBoundary/WithErrorBoundary';

import type { HeaderProps as HeaderGCProps } from '@skyscanner-internal/global-components/header';
import type { PartialAuthClient } from '@skyscanner-internal/global-components/login-form/types/auth-client';
import type { HeaderProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/Header';

type Nullable<T> = T | null;

const Header = (props: HeaderProps) => {
  const [isLoginFormVisible, setIsLoginFormVisible] = useState(false);

  const [authClient, setAuthClient] =
    useState<Nullable<PartialAuthClient>>(null);
  const [subscriptionClient, setSubscriptionClient] =
    useState<Nullable<SubscriptionClient>>(null);

  useEffect(() => {
    loginModalInit({ setLoginFormVisible: setIsLoginFormVisible });

    // eslint-disable-next-line global-require, import/no-unresolved
    const AuthClient = require('@skyscanner/auth-client').default;

    // Initialize the Auth Client and Subscription Client within the useEffect to ensure it
    // is created only on the client side and not during server-side rendering.
    // We're using state so we re-render.
    setSubscriptionClient(new SubscriptionClient());

    setAuthClient(
      new AuthClient({
        locale: props.locale,
        market: props.market,
        authConsumer: 'falcon',
        env: props.isProductionEnv ? 'production' : 'development',
      }),
    );
  }, [props.locale, props.market, props.isProductionEnv]);

  const changeLoginFormVisibility = ({
    shouldBeOpen,
  }: {
    shouldBeOpen?: boolean;
    loginFlowComplete?: boolean;
  }) => {
    setIsLoginFormVisible(Boolean(shouldBeOpen));
  };

  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(
    fetchCultureSelectorData,
    props.locale ?? 'en-GB',
    false,
  );

  const { cultureSelectorEnStrings, cultureSelectorStrings, ...filteredProps } =
    props;

  const userLoggedIn = isBrowser() ? isLoggedIn() : undefined;
  const profilePictureFn = isBrowser() ? getProfilePicture : undefined;

  const headerGCProps: HeaderGCProps = {
    logger,
    userLoggedIn,
    getProfilePicture: profilePictureFn,
    ...filteredProps,
    cultureSelectorProps: {
      markets,
      locales,
      currencies,
      enMarkets,
      isProductionEnv: props.isProductionEnv,
      strings: cultureSelectorStrings,
      enStrings: cultureSelectorEnStrings,
      persistUserSessionDataFn:
        props.enableAnonymousUtidPropagationOnCultureSelectorTldRedirect
          ? persistUserSessionData
          : undefined,
    },
    loginFormProps:
      authClient && subscriptionClient
        ? {
            strings: props.loginFormStrings,
            isProductionEnv: props.isProductionEnv,
            authClient,
            isOpen: isLoginFormVisible,
            onVisibilityChange: changeLoginFormVisibility,
            subscriptionClient,
            source: SOURCES.HEADER as Source,
            consumer: 'falcon',
          }
        : undefined,
    isFetchingCultureSelectorData,
    onCultureSelectorButtonClick,
  };

  return (
    <div id="header-root">
      <HeaderGC {...headerGCProps} />
    </div>
  );
};

const HeaderWithErrorBoundary = withErrorBoundary(Header, 'header');

export default HeaderWithErrorBoundary;
